/**
 * AnteTrek realtime popup widget helpers.
 *
 * The integration is enabled per deployment by REACT_APP_ANTETREK_BASE_URL
 * (e.g. https://staging.antetrek.com); script, WebSocket and API URLs are
 * derived from it. These values are expected to come from the backend later,
 * so every env read stays inside this file.
 *
 * The widget script exposes `window.CrmPopupWidget.connect({ wsUrl, apiBaseUrl, token })`,
 * where `token` is the short-lived connect-token minted by GET /user/antetrek-popup-token.
 */

export const ANTETREK_SCRIPT_ID = 'antetrek-popup-widget-script'
export const ANTETREK_REFRESH_MARGIN_MS = 60 * 1000 // refresh this long before expiresAt
export const ANTETREK_MIN_REFRESH_DELAY_MS = 5 * 1000
export const ANTETREK_RETRY_DELAY_MS = 30 * 1000 // retry cadence after a failed refresh
export const ANTETREK_READY_POLL_MS = 300
export const ANTETREK_READY_TIMEOUT_MS = 15 * 1000
const MAX_TIMEOUT_MS = 2147483647 // setTimeout caps at 2^31 - 1

export const ANTETREK_POPUP_INITIAL_STATE = Object.freeze({
  loading: false,
  enabled: false,
  token: null,
  expiresAt: null,
  failedAt: null
})

const getBaseUrl = () => (process.env.REACT_APP_ANTETREK_BASE_URL || '').replace(/\/+$/, '')

export const isAntetrekPopupEnabled = () => Boolean(getBaseUrl())

export const getAntetrekUrls = () => {
  const baseUrl = getBaseUrl()
  const { host } = new URL(baseUrl)
  return {
    scriptUrl: `${baseUrl}/popup-widget.js`,
    wsUrl: `wss://${host}/popup`,
    apiBaseUrl: `${baseUrl}/api/v1`
  }
}

export const isAntetrekReady = () => typeof window.CrmPopupWidget?.connect === 'function'

/**
 * Inject the widget script once. On a load error the tag is removed so a later
 * attempt (next token) can retry.
 */
export const loadAntetrekScript = () => {
  if (document.getElementById(ANTETREK_SCRIPT_ID)) return
  const script = document.createElement('script')
  script.id = ANTETREK_SCRIPT_ID
  script.src = getAntetrekUrls().scriptUrl
  script.async = true
  script.onerror = () => {
    script.remove()
    console.error('AnteTrek popup widget script failed to load')
  }
  document.head.appendChild(script)
}

/**
 * Run `cb` once the widget global is available (immediately if it already is).
 * Polls briefly and gives up after ANTETREK_READY_TIMEOUT_MS. Returns a cleanup.
 */
export const onAntetrekReady = (cb) => {
  if (isAntetrekReady()) {
    cb()
    return () => {}
  }

  let interval = null
  let timeout = null
  const stop = () => {
    clearInterval(interval)
    clearTimeout(timeout)
  }
  interval = setInterval(() => {
    if (isAntetrekReady()) {
      stop()
      cb()
    }
  }, ANTETREK_READY_POLL_MS)
  timeout = setTimeout(stop, ANTETREK_READY_TIMEOUT_MS)

  return stop
}

export const connectAntetrek = (token) => {
  const { wsUrl, apiBaseUrl } = getAntetrekUrls()
  window.CrmPopupWidget.connect({ wsUrl, apiBaseUrl, token })
}

// The widget's teardown API is unconfirmed: call it when present, ignore otherwise.
export const disconnectAntetrek = () => {
  try {
    window.CrmPopupWidget?.disconnect?.()
  } catch (error) {
    console.error('AnteTrek popup widget disconnect failed', error)
  }
}

const parseExpiry = (expiresAt) => {
  const ms = Date.parse(expiresAt)
  return Number.isNaN(ms) ? null : ms
}

/** Delay (ms) until the token should be refreshed, or null when expiresAt is missing/invalid. */
export const getAntetrekRefreshDelay = (expiresAt, now = Date.now()) => {
  const expiryMs = parseExpiry(expiresAt)
  if (expiryMs === null) return null
  const delay = expiryMs - now - ANTETREK_REFRESH_MARGIN_MS
  return Math.min(Math.max(delay, ANTETREK_MIN_REFRESH_DELAY_MS), MAX_TIMEOUT_MS)
}

/** True once the token is inside its refresh margin (or already expired). */
export const isAntetrekTokenStale = (expiresAt, now = Date.now()) => {
  const expiryMs = parseExpiry(expiresAt)
  if (expiryMs === null) return false
  return now >= expiryMs - ANTETREK_REFRESH_MARGIN_MS
}

/** True once expiresAt has passed (or is unusable): there is nothing left to protect with a retry. */
export const isAntetrekTokenExpired = (expiresAt, now = Date.now()) => {
  const expiryMs = parseExpiry(expiresAt)
  return expiryMs === null || now >= expiryMs
}
